// preferance.js (React + Tailwind) — BACKEND ENTEGRE
// ✅ LocalStorage ile tercih saklama yok (sadece auth için userToken + card kullanılır)
// ✅ Sayfa açılınca backend'den çekilir, çekilene kadar loading screen gösterilir
// ✅ TEK DOKÜMAN: collection "preferance" (doc id = card)
// ✅ Fields:
//    preapprove(boolean),
//    cash(boolean), multinet(boolean), sodexo(boolean), setcard(boolean), edenred(boolean), metropol(boolean),
//    ekuno(number)
// ✅ Kaydet -> backend'e yazar
// ✅ Header'da butonlar: Kaydet + Fiş Bilgi + Ürün Paneli + Dizayn Paneli
// ✅ Kaydedilmemiş değişiklik varken nav butonlarına basılırsa confirm modal çıkar
//
// ✅ UPDATE:
// - Genel "Yemek kartı gösterilsin mi?" kaldırıldı
// - yemekkart field tamamen kaldırıldı
// - Sadece provider bazlı seçenekler kaldı
// - "Nakit & Yemek Kartı: KDS ödeme tamamlandı otomatik olsun mu?" => preapprove
// - Nakit => cash
// - Multinet => multinet
// - Sodexo => sodexo
// - Setcard => setcard
// - Edenred => edenred
// - Metropol => metropol
// - EKÜ No => ekuno

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

// ✅ Cloud Functions base
const API_BASE_URL = (
  (typeof process !== "undefined" && process.env?.REACT_APP_MERCHANT_API_BASE_URL) ||
  "https://kdspanel-production.up.railway.app/api/merchant"
).replace(/\/+$/, "");

// ✅ Token & Card
const getAuthToken = () => localStorage.getItem("userToken");
const getAuthCard = () => localStorage.getItem("card");

const redirectToLogin = () => {
  localStorage.removeItem("userToken");
  localStorage.removeItem("card");
  window.location.href = "/mlogin";
};

// =====================================================
// ✅ HELPERS
// =====================================================
function normalizeEkuNo(value) {
  if (value === "" || value === null || value === undefined) return "1";

  const onlyDigits = String(value).replace(/[^\d]/g, "");
  if (!onlyDigits) return "1";

  const n = Number(onlyDigits);
  if (!Number.isFinite(n) || n < 1) return "1";

  return String(Math.trunc(n));
}


const RECOMMENDATION_LANGUAGE_CODES = [
  "tr", "en", "de", "fr", "es", "it", "pt", "ru", "ar", "fa", "ur", "az", "kk", "uz", "tk",
  "ky", "tg", "zh", "ja", "ko", "hi", "bn", "pa", "gu", "mr", "ne", "si", "ta", "te", "kn",
  "ml", "th", "vi", "id", "ms", "fil", "my", "km", "lo", "he", "el", "bg", "ro", "hu", "pl",
  "cs", "sk", "sl", "hr", "sr", "bs", "mk", "sq", "uk", "be", "lt", "lv", "et", "fi", "sv",
  "no", "da", "is", "nl", "af", "sw", "am", "so", "ha", "yo", "ig", "zu", "xh", "rw", "mg",
  "ka", "hy", "eu", "ca", "gl", "cy", "ga", "mt", "lb", "eo", "la", "mn", "ps", "ku", "sd",
  "jv", "su", "mi", "sm", "to", "ht"
];

const RECOMMENDATION_LANGUAGE_FALLBACKS = {
  tr: "Türkçe", en: "English", de: "Deutsch", fr: "Français", es: "Español", it: "Italiano",
  pt: "Português", ru: "Русский", ar: "العربية", fa: "فارسی", az: "Azərbaycan dili", zh: "中文",
  ja: "日本語", ko: "한국어", hi: "हिन्दी",
};

const RECOMMENDATION_STANDARD_DURATION_OPTIONS = [
  { value: "", label: "Seçilmedi" },
  { value: "0", label: "+0" },
  { value: "15m", label: "+15 dk" },
  { value: "30m", label: "+30 dk" },
  { value: "45m", label: "+45 dk" },
  { value: "1h", label: "+1 saat" },
  { value: "2h", label: "+2 saat" },
  { value: "3h", label: "+3 saat" },
  { value: "4h", label: "+4 saat" },
  { value: "all_day", label: "Tüm gün" },
];
const RECOMMENDATION_DAY_DURATION_OPTIONS = [
  { value: "", label: "Seçilmedi" },
  { value: "0", label: "+0" },
  { value: "1d", label: "+1 gün" },
  { value: "2d", label: "+2 gün" },
  { value: "3d", label: "+3 gün" },
  { value: "4d", label: "+4 gün" },
  { value: "5d", label: "+5 gün" },
  { value: "7d", label: "+7 gün" },
  { value: "10d", label: "+10 gün" },
  { value: "15d", label: "+15 gün" },
  { value: "1mo", label: "+1 ay" },
];
const RECOMMENDATION_STANDARD_DURATION_VALUES = new Set(RECOMMENDATION_STANDARD_DURATION_OPTIONS.map((x) => x.value));
const RECOMMENDATION_DAY_DURATION_VALUES = new Set(RECOMMENDATION_DAY_DURATION_OPTIONS.map((x) => x.value));
const RECOMMENDATION_DURATION_VALUES = new Set([...RECOMMENDATION_STANDARD_DURATION_VALUES, ...RECOMMENDATION_DAY_DURATION_VALUES]);
const getRecommendationDurationOptions = (dayEnabled) => dayEnabled ? RECOMMENDATION_DAY_DURATION_OPTIONS : RECOMMENDATION_STANDARD_DURATION_OPTIONS;
const RECOMMENDATION_VAT_RATES = [10, 20, 1, 0];

const normalizeRecommendationLanguageCode = (value, fallback = "tr") => {
  const code = String(value || fallback).trim().toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 20);
  return code || fallback;
};

const getRecommendationLanguageLabel = (code) => {
  const safeCode = normalizeRecommendationLanguageCode(code);
  if (RECOMMENDATION_LANGUAGE_FALLBACKS[safeCode]) return RECOMMENDATION_LANGUAGE_FALLBACKS[safeCode];
  try {
    const value = new Intl.DisplayNames([safeCode], { type: "language" }).of(safeCode);
    if (value && value !== safeCode) return value.charAt(0).toLocaleUpperCase(safeCode) + value.slice(1);
  } catch {}
  return safeCode.toUpperCase();
};

const RECOMMENDATION_LANGUAGE_OPTIONS = RECOMMENDATION_LANGUAGE_CODES.map((code) => ({
  code,
  label: getRecommendationLanguageLabel(code),
})).sort((a, b) => a.label.localeCompare(b.label, "tr"));

const roundRecommendationMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round((n + Number.EPSILON) * 100) / 100;
};

const normalizeRecommendationDiscountRate = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, Math.round((n + Number.EPSILON) * 100) / 100));
};

const normalizeRecommendationDepartmentNo = (value) => String(value ?? "").replace(/\D/g, "").slice(0, 6);
const normalizeRecommendationDuration = (value, dayEnabled = false) => {
  const clean = String(value ?? "").trim().toLowerCase();
  const allowed = dayEnabled ? RECOMMENDATION_DAY_DURATION_VALUES : RECOMMENDATION_STANDARD_DURATION_VALUES;
  return allowed.has(clean) ? clean : "";
};

const createEmptyRecommendationLocale = (languageCode = "tr") => {
  const code = normalizeRecommendationLanguageCode(languageCode);
  return {
    languageCode: code,
    language: getRecommendationLanguageLabel(code),
    name: "",
    description: "",
    basePrice: 0,
    kdv: 10,
    discountEnabled: false,
    discountRate: 0,
    departmanno: "",
    hasQuantity: false,
    personnelEnabled: false,
    timeEnabled: false,
    dayEnabled: false,
    durationEnabled: false,
    durationVisible: true,
    durationValue: "",
    imageUrl: "",
    videoUrl: "",
    mediaType: "none",
    mediaUrl: "",
    videoId: "",
    mediaVersion: 1,
    mediaUpdatedAt: null,
    active: true,
  };
};

const normalizeRecommendationLocale = (raw = {}, languageCode = "tr") => {
  const code = normalizeRecommendationLanguageCode(raw?.languageCode || languageCode);
  const legacyMediaType = raw?.mediaType === "video" ? "video" : raw?.mediaType === "image" ? "image" : "none";
  const legacyMediaUrl = String(raw?.mediaUrl || "");
  const imageUrl = String(
    raw?.imageUrl || raw?.posterUrl || raw?.thumbnailUrl || raw?.thumbUrl ||
    (legacyMediaType === "image" ? legacyMediaUrl : raw?.image || raw?.photoUrl || "")
  );
  const videoUrl = String(raw?.videoUrl || (legacyMediaType === "video" ? legacyMediaUrl : ""));
  const mediaType = videoUrl ? "video" : imageUrl ? "image" : "none";
  const dayEnabled = !!raw?.dayEnabled;
  const durationEnabled = !!raw?.durationEnabled;
  return {
    ...createEmptyRecommendationLocale(code),
    ...raw,
    languageCode: code,
    language: getRecommendationLanguageLabel(code),
    name: String(raw?.name || "").slice(0, 100),
    description: String(raw?.description || raw?.desc || "").slice(0, 1000),
    basePrice: roundRecommendationMoney(raw?.basePrice ?? raw?.price ?? 0),
    kdv: RECOMMENDATION_VAT_RATES.includes(Number(raw?.kdv)) ? Number(raw.kdv) : 10,
    discountEnabled: !!raw?.discountEnabled,
    discountRate: normalizeRecommendationDiscountRate(raw?.discountRate),
    departmanno: normalizeRecommendationDepartmentNo(raw?.departmanno ?? raw?.departmentNo ?? raw?.departmentno ?? ""),
    hasQuantity: !!raw?.hasQuantity,
    personnelEnabled: dayEnabled ? false : !!raw?.personnelEnabled,
    timeEnabled: dayEnabled ? false : !!raw?.timeEnabled,
    dayEnabled,
    durationEnabled,
    durationVisible: raw?.durationVisible !== false,
    durationValue: durationEnabled ? normalizeRecommendationDuration(raw?.durationValue, dayEnabled) : "",
    imageUrl,
    videoUrl,
    mediaType,
    mediaUrl: videoUrl || imageUrl,
    videoId: videoUrl ? String(raw?.videoId || "").replace(/\D/g, "").slice(0, 30) : "",
    mediaVersion: Math.max(1, Number(raw?.mediaVersion || raw?.videoVersion || 1)),
    mediaUpdatedAt: raw?.mediaUpdatedAt || raw?.videoUpdatedAt || null,
    active: raw?.active !== false,
  };
};

const createRecommendationProduct = () => ({
  id: `rec_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
  languages: ["tr"],
  localizedData: [createEmptyRecommendationLocale("tr")],
});

const normalizeRecommendationProduct = (raw = {}) => {
  const source = Array.isArray(raw?.localizedData) ? raw.localizedData : [];
  const seen = new Set();
  const localizedData = [];
  source.forEach((locale) => {
    const code = normalizeRecommendationLanguageCode(locale?.languageCode || "tr");
    if (seen.has(code)) return;
    seen.add(code);
    localizedData.push(normalizeRecommendationLocale(locale, code));
  });
  if (!seen.has("tr")) localizedData.unshift(normalizeRecommendationLocale(raw, "tr"));
  return {
    id: String(raw?.id || raw?.recommendationId || `rec_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`).slice(0, 100),
    languages: localizedData.map((x) => x.languageCode),
    localizedData,
  };
};

const normalizeRecommendedProducts = (value) => (Array.isArray(value) ? value : []).map(normalizeRecommendationProduct);

const normalizeRecommendationHeadingLocalizedData = (value) => {
  const source = Array.isArray(value) ? value : [];
  const seen = new Set();
  const out = [];
  source.forEach((raw) => {
    const languageCode = normalizeRecommendationLanguageCode(raw?.languageCode || "tr");
    if (seen.has(languageCode)) return;
    seen.add(languageCode);
    out.push({
      languageCode,
      language: getRecommendationLanguageLabel(languageCode),
      text: String(raw?.text || raw?.title || "").slice(0, 180),
    });
  });
  if (!seen.has("tr")) {
    out.unshift({ languageCode: "tr", language: "Türkçe", text: "Bu hizmeti alanlar bunları da ekledi" });
  }
  return out;
};

const parsePreferenceDepartments = (value) => {
  const input = Array.isArray(value)
    ? value
    : typeof value === "string" && value.trim()
      ? [value]
      : [];

  const seen = new Set();
  const out = [];

  for (const item of input) {
    let parsed = null;

    if (item && typeof item === "object") {
      const number = normalizeRecommendationDepartmentNo(
        item.number ?? item.departmanno ?? item.departmentNo ?? item.departmentno ?? item.id
      );
      const name = String(item.name || item.departmentName || item.title || item.label || "Departman").trim();
      const vat = item.vatRate ?? item.kdv ?? item.vat ?? "";

      if (number) {
        parsed = {
          number,
          label: `${name} - Departman ${number}${vat !== undefined && vat !== "" ? ` - KDV %${vat}` : ""}`,
        };
      }
    } else {
      const raw = String(item || "").trim();
      if (raw) {
        // Fiş Bilgi ekranındaki gerçek saklama biçimi: "isim,departmanNo,kdv"
        // Örnek: "su,1,1" veya "yiyecek,2,10".
        const [csvName = "", csvNumber = "", csvVat = ""] = raw.split(",");
        const csvDepartmentNo = normalizeRecommendationDepartmentNo(csvNumber);

        if (csvName.trim() && csvDepartmentNo) {
          parsed = {
            number: csvDepartmentNo,
            label: `${csvName.trim()} - Departman ${csvDepartmentNo}${csvVat.trim() ? ` - KDV %${csvVat.trim()}` : ""}`,
          };
        } else {
          // Eski/alternatif görüntü formatlarıyla geriye dönük uyumluluk.
          const numberMatch = raw.match(/departman\s*(\d{1,6})/i) || raw.match(/-\s*(\d{1,6})\s*(?:-|$)/);
          const number = normalizeRecommendationDepartmentNo(numberMatch?.[1] || "");
          if (number) parsed = { number, label: raw };
        }
      }
    }

    if (!parsed || seen.has(parsed.number)) continue;
    seen.add(parsed.number);
    out.push(parsed);
  }

  return out;
};

const recommendationFileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const raw = String(reader.result || "");
    resolve(raw.includes(",") ? raw.split(",").pop() : raw);
  };
  reader.onerror = () => reject(reader.error || new Error("Dosya okunamadı"));
  reader.readAsDataURL(file);
});

const recommendationDiscountBreakdown = (locale) => {
  const original = roundRecommendationMoney(locale?.basePrice || 0);
  const rate = locale?.discountEnabled ? normalizeRecommendationDiscountRate(locale?.discountRate) : 0;
  const effective = locale?.discountEnabled ? roundRecommendationMoney(original * (1 - rate / 100)) : original;
  return { original, rate, effective, amount: roundRecommendationMoney(original - effective) };
};

// =====================================================
// ✅ API HELPERS
// =====================================================
async function apiPost(path, body, { signal } = {}) {
  const token = getAuthToken();
  if (!token) {
    redirectToLogin();
    throw new Error("TOKEN_MISSING");
  }

  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body || {}),
    signal,
  });

  let json = null;
  try {
    json = await res.json();
  } catch {
    // ignore
  }

  if (json?.redirectToLogin || res.status === 401) {
    redirectToLogin();
    throw new Error("AUTH_FAILED");
  }

  if (!res.ok || json?.success === false) {
    const msg = json?.message || json?.error || `HTTP_${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.payload = json;
    throw err;
  }

  return json;
}

// =====================================================
// ✅ ENDPOINT CONTRACT
// =====================================================
// POST /getpreferance
// body: { card }
// -> data: {
//      preapprove:boolean,
//      cash:boolean,
//      multinet:boolean,
//      sodexo:boolean,
//      setcard:boolean,
//      edenred:boolean,
//      metropol:boolean,
//      ekuno:number
//    }
//
// POST /savepreferance
// body: {
//   card,
//   preapprove:boolean,
//   cash:boolean,
//   multinet:boolean,
//   sodexo:boolean,
//   setcard:boolean,
//   edenred:boolean,
//   metropol:boolean,
//   ekuno:number
// }
// -> success:true

// =====================================================
// ✅ UI COMPONENTS
// =====================================================
export const DEFAULT_PREFERENCES = Object.freeze({
  // UI Toggle -> preapprove
  kdsAutoPaidOnCashOrMeal: false,

  // Ödeme seçeneği toggle'ları
  // Eski kayıtlarda cash alanı olmadığı için geriye dönük olarak açık başlar.
  showCash: true,
  showMultinet: false,
  showSodexo: false,
  showSetcard: false,
  showEdenred: false,
  showMetropol: false,

  // EKÜ No
  ekuNo: "1",

  // Tüm ana ürünlerin ortak kullandığı öneri kataloğu
  recommendedHeadingLocalizedData: [
    { languageCode: "tr", language: "Türkçe", text: "Bu hizmeti alanlar bunları da ekledi" },
  ],
  recommendedProducts: [],

  // Rezervasyonu Tamamla butonundan sonra gösterilen ikinci ortak ekstra kataloğu
  reservationCompleteExtraHeadingLocalizedData: [
    { languageCode: "tr", language: "Türkçe", text: "Rezervasyonunuza bunları da eklemek ister misiniz?" },
  ],
  reservationCompleteExtraProducts: [],
});

const EXTRA_PAYMENT_OPTIONS = [
  {
    stateKey: "showCash",
    title: "Nakit",
    description: "Nakit ödeme seçeneği ödeme ekranında gösterilsin mi?",
  },
  {
    stateKey: "showMultinet",
    title: "Multinet",
    description: "Multinet ödeme seçeneği ödeme ekranında gösterilsin mi?",
  },
  {
    stateKey: "showSodexo",
    title: "Sodexo",
    description: "Sodexo ödeme seçeneği ödeme ekranında gösterilsin mi?",
  },
  {
    stateKey: "showSetcard",
    title: "Setcard",
    description: "Setcard ödeme seçeneği ödeme ekranında gösterilsin mi?",
  },
  {
    stateKey: "showEdenred",
    title: "Edenred",
    description: "Edenred ödeme seçeneği ödeme ekranında gösterilsin mi?",
  },
  {
    stateKey: "showMetropol",
    title: "Metropol",
    description: "Metropol ödeme seçeneği ödeme ekranında gösterilsin mi?",
  },
];

export function ToggleSwitch({
  checked,
  disabled,
  onChange,
  onLabel = "Açık",
  offLabel = "Kapalı",
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={[
        "relative inline-flex h-10 w-[86px] items-center rounded-full border-2 transition-all duration-300 select-none",
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
        checked ? "bg-emerald-500 border-emerald-600" : "bg-gray-200 border-gray-300",
      ].join(" ")}
      aria-pressed={checked}
      aria-label="toggle"
    >
      <span
        className={[
          "inline-block h-8 w-8 rounded-full bg-white shadow-md transition-all duration-300",
          checked ? "translate-x-[46px]" : "translate-x-1",
        ].join(" ")}
      />
      <span className="absolute left-3 text-xs font-bold text-white">
        {checked ? onLabel : ""}
      </span>
      <span className="absolute right-3 text-xs font-bold text-gray-700">
        {!checked ? offLabel : ""}
      </span>
    </button>
  );
}

export function PreferenceCard({ title, description, children }) {
  return (
    <div className="bg-white rounded-3xl border-2 border-gray-200 shadow-sm p-6">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5">
        <div className="min-w-0 flex-1">
          <div className="text-xl font-extrabold text-gray-900">{title}</div>
          {description ? (
            <div className="text-gray-600 mt-2 leading-relaxed whitespace-pre-line">
              {description}
            </div>
          ) : null}
        </div>
        <div className="shrink-0">{children}</div>
      </div>
    </div>
  );
}

function ConfirmLeaveModal({
  open,
  onClose,
  onDiscard,
  onSaveAndContinue,
  hasChanges,
  saving,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative w-[92%] max-w-lg rounded-3xl bg-white shadow-2xl border-2 border-gray-100 p-6">
        <div className="text-2xl font-extrabold text-gray-900">
          Değişiklikleri kaydetmeden çıkmak mı istiyorsunuz?
        </div>
        <div className="mt-2 text-gray-600 leading-relaxed">
          {hasChanges
            ? "Kaydedilmemiş değişiklikler var. Devam ederseniz bu değişiklikler kaybolacak."
            : "Devam etmek istiyor musunuz?"}
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:justify-end">
          <button
            type="button"
            onClick={onDiscard}
            className="rounded-2xl border-2 border-gray-300 bg-white px-5 py-3 font-semibold text-gray-800 shadow-sm hover:shadow-md transition"
          >
            Kaydetmeden devam
          </button>

          <button
            type="button"
            disabled={saving}
            onClick={onSaveAndContinue}
            className={[
              "rounded-2xl px-5 py-3 font-semibold text-white shadow-sm transition",
              saving
                ? "bg-indigo-300 cursor-not-allowed"
                : "bg-indigo-600 hover:bg-indigo-700 hover:shadow-md",
            ].join(" ")}
          >
            Kaydet ve devam et
          </button>

          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl px-5 py-3 font-semibold text-white shadow-sm transition bg-gray-800 hover:bg-gray-900 hover:shadow-md"
          >
            İptal
          </button>
        </div>
      </div>
    </div>
  );
}

function FullscreenLoading({ text = "Tercihler yükleniyor..." }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-6">
      <div className="bg-white border-2 border-gray-200 rounded-3xl shadow-sm p-6 w-full max-w-md">
        <div className="flex items-center gap-3">
          <div className="h-6 w-6 rounded-full border-2 border-gray-300 border-t-gray-900 animate-spin" />
          <div className="font-semibold text-gray-900">{text}</div>
        </div>
        <div className="mt-2 text-sm text-gray-600">Lütfen bekleyin…</div>
      </div>
    </div>
  );
}

// =====================================================
// ✅ MAIN PAGE
// =====================================================
export function Preferance({
  onSave,
  onChange,
  showHeader = true,
  title = "Tercihler",
}) {
  const navigate = useNavigate();

  const [prefs, setPrefs] = useState({ ...DEFAULT_PREFERENCES });
  const [savedPrefs, setSavedPrefs] = useState({ ...DEFAULT_PREFERENCES });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState("");
  const [error, setError] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);

  const [departments, setDepartments] = useState([]);
  const [recommendationDraft, setRecommendationDraft] = useState(null);
  const [recommendationDraftCatalog, setRecommendationDraftCatalog] = useState("recommended");
  const [activeRecommendationLanguage, setActiveRecommendationLanguage] = useState("tr");
  const [recommendationLanguageToAdd, setRecommendationLanguageToAdd] = useState("");
  const [headingLanguage, setHeadingLanguage] = useState("tr");
  const [headingLanguageToAdd, setHeadingLanguageToAdd] = useState("");
  const [reservationCompleteHeadingLanguage, setReservationCompleteHeadingLanguage] = useState("tr");
  const [reservationCompleteHeadingLanguageToAdd, setReservationCompleteHeadingLanguageToAdd] = useState("");
  const [uploadingRecommendationMedia, setUploadingRecommendationMedia] = useState(false);

  const hasChanges = useMemo(() => {
    return (
      prefs.kdsAutoPaidOnCashOrMeal !== savedPrefs.kdsAutoPaidOnCashOrMeal ||
      prefs.showCash !== savedPrefs.showCash ||
      prefs.showMultinet !== savedPrefs.showMultinet ||
      prefs.showSodexo !== savedPrefs.showSodexo ||
      prefs.showSetcard !== savedPrefs.showSetcard ||
      prefs.showEdenred !== savedPrefs.showEdenred ||
      prefs.showMetropol !== savedPrefs.showMetropol ||
      normalizeEkuNo(prefs.ekuNo) !== normalizeEkuNo(savedPrefs.ekuNo) ||
      JSON.stringify(normalizeRecommendationHeadingLocalizedData(prefs.recommendedHeadingLocalizedData)) !==
        JSON.stringify(normalizeRecommendationHeadingLocalizedData(savedPrefs.recommendedHeadingLocalizedData)) ||
      JSON.stringify(normalizeRecommendedProducts(prefs.recommendedProducts)) !==
        JSON.stringify(normalizeRecommendedProducts(savedPrefs.recommendedProducts)) ||
      JSON.stringify(normalizeRecommendationHeadingLocalizedData(prefs.reservationCompleteExtraHeadingLocalizedData)) !==
        JSON.stringify(normalizeRecommendationHeadingLocalizedData(savedPrefs.reservationCompleteExtraHeadingLocalizedData)) ||
      JSON.stringify(normalizeRecommendedProducts(prefs.reservationCompleteExtraProducts)) !==
        JSON.stringify(normalizeRecommendedProducts(savedPrefs.reservationCompleteExtraProducts))
    );
  }, [prefs, savedPrefs]);

  useEffect(() => {
    onChange?.(prefs);
  }, [prefs, onChange]);

  const loadFromBackend = useCallback(async ({ signal } = {}) => {
    const card = getAuthCard();
    if (!card) {
      redirectToLogin();
      return;
    }

    setError("");
    setLoading(true);

    try {
      const [resp, fisResp] = await Promise.all([
        apiPost("/getpreferance", { card }, { signal }),
        apiPost("/getfisbilgi", { card }, { signal }).catch(() => null),
      ]);

      const data = resp?.data || {};
      setDepartments(parsePreferenceDepartments(fisResp?.data?.departments || []));

      const merged = {
        kdsAutoPaidOnCashOrMeal: !!data?.preapprove,
        // cash alanı bulunmayan eski kayıtlarda Nakit açık kalır.
        showCash: data?.cash !== false,
        showMultinet: !!data?.multinet,
        showSodexo: !!data?.sodexo,
        showSetcard: !!data?.setcard,
        showEdenred: !!data?.edenred,
        showMetropol: !!data?.metropol,
        ekuNo: normalizeEkuNo(data?.ekuno),
        recommendedHeadingLocalizedData: normalizeRecommendationHeadingLocalizedData(data?.recommendedHeadingLocalizedData),
        recommendedProducts: normalizeRecommendedProducts(data?.recommendedProducts),
        reservationCompleteExtraHeadingLocalizedData: normalizeRecommendationHeadingLocalizedData(
          Array.isArray(data?.reservationCompleteExtraHeadingLocalizedData) && data.reservationCompleteExtraHeadingLocalizedData.length
            ? data.reservationCompleteExtraHeadingLocalizedData
            : [{ languageCode: "tr", language: "Türkçe", text: "Rezervasyonunuza bunları da eklemek ister misiniz?" }]
        ),
        reservationCompleteExtraProducts: normalizeRecommendedProducts(data?.reservationCompleteExtraProducts),
      };

      setPrefs(merged);
      setSavedPrefs(merged);
    } catch (e) {
      setError(`❌ Tercihler alınamadı: ${e?.message || "Bilinmeyen hata"}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    loadFromBackend({ signal: ac.signal });
    return () => ac.abort();
  }, [loadFromBackend]);

  const handleSave = useCallback(async () => {
    const card = getAuthCard();
    if (!card) {
      redirectToLogin();
      return false;
    }

    const normalizedEkuNo = normalizeEkuNo(prefs.ekuNo);

    if (!normalizedEkuNo || Number(normalizedEkuNo) < 1) {
      setError("❌ EKÜ No en az 1 olmalıdır.");
      return false;
    }

    setError("");
    setSaving(true);

    try {
      await apiPost("/savepreferance", {
        card,
        preapprove: !!prefs.kdsAutoPaidOnCashOrMeal,
        cash: !!prefs.showCash,
        multinet: !!prefs.showMultinet,
        sodexo: !!prefs.showSodexo,
        setcard: !!prefs.showSetcard,
        edenred: !!prefs.showEdenred,
        metropol: !!prefs.showMetropol,
        ekuno: Number(normalizedEkuNo),
        recommendedHeadingLocalizedData: normalizeRecommendationHeadingLocalizedData(prefs.recommendedHeadingLocalizedData),
        recommendedProducts: normalizeRecommendedProducts(prefs.recommendedProducts),
        reservationCompleteExtraHeadingLocalizedData: normalizeRecommendationHeadingLocalizedData(prefs.reservationCompleteExtraHeadingLocalizedData),
        reservationCompleteExtraProducts: normalizeRecommendedProducts(prefs.reservationCompleteExtraProducts),
      });

      const nextSaved = {
        ...prefs,
        ekuNo: normalizedEkuNo,
      };

      setPrefs(nextSaved);
      setSavedPrefs(nextSaved);
      onSave?.(nextSaved);

      setToast("✅ Tercihler kaydedildi.");
      setTimeout(() => setToast(""), 2200);
      return true;
    } catch (e) {
      setError(`❌ Kaydetme sırasında hata: ${e?.message || "Bilinmeyen hata"}`);
      return false;
    } finally {
      setSaving(false);
    }
  }, [prefs, onSave]);

  const handleResetToDefault = () => {
    setError("");
    setPrefs({ ...DEFAULT_PREFERENCES });
  };


  const openNewRecommendation = (catalog = "recommended") => {
    setRecommendationDraftCatalog(catalog === "reservationComplete" ? "reservationComplete" : "recommended");
    setRecommendationDraft(createRecommendationProduct());
    setActiveRecommendationLanguage("tr");
    setRecommendationLanguageToAdd("");
  };

  const openEditRecommendation = (item, catalog = "recommended") => {
    setRecommendationDraftCatalog(catalog === "reservationComplete" ? "reservationComplete" : "recommended");
    setRecommendationDraft(JSON.parse(JSON.stringify(normalizeRecommendationProduct(item))));
    setActiveRecommendationLanguage("tr");
    setRecommendationLanguageToAdd("");
  };

  const updateRecommendationDraftLocale = (languageCode, updater) => {
    const code = normalizeRecommendationLanguageCode(languageCode);
    setRecommendationDraft((previous) => {
      if (!previous) return previous;
      const normalized = normalizeRecommendationProduct(previous);
      const localizedData = [...normalized.localizedData];
      const index = localizedData.findIndex((x) => x.languageCode === code);
      const current = index >= 0 ? localizedData[index] : createEmptyRecommendationLocale(code);
      const nextRaw = typeof updater === "function" ? updater({ ...current }) : { ...current, ...(updater || {}) };
      const next = normalizeRecommendationLocale(nextRaw, code);
      if (index >= 0) localizedData[index] = next;
      else localizedData.push(next);
      return { ...normalized, languages: localizedData.map((x) => x.languageCode), localizedData };
    });
  };

  const addRecommendationLanguage = () => {
    if (!recommendationDraft || !recommendationLanguageToAdd) return;
    const code = normalizeRecommendationLanguageCode(recommendationLanguageToAdd);
    if (recommendationDraft.localizedData?.some((x) => x.languageCode === code)) return;
    updateRecommendationDraftLocale(code, createEmptyRecommendationLocale(code));
    setActiveRecommendationLanguage(code);
    setRecommendationLanguageToAdd("");
  };

  const removeRecommendationLanguage = (code) => {
    if (!recommendationDraft || code === "tr") return;
    setRecommendationDraft((previous) => {
      const normalized = normalizeRecommendationProduct(previous || {});
      const localizedData = normalized.localizedData.filter((x) => x.languageCode !== code);
      return { ...normalized, localizedData, languages: localizedData.map((x) => x.languageCode) };
    });
    setActiveRecommendationLanguage((current) => current === code ? "tr" : current);
  };

  const saveRecommendationDraft = () => {
    if (!recommendationDraft) return;
    const normalized = normalizeRecommendationProduct(recommendationDraft);
    const missingName = normalized.localizedData.find((x) => !String(x.name || "").trim());
    if (missingName) {
      setError(`❌ ${getRecommendationLanguageLabel(missingName.languageCode)} öneri ürününün adını girin.`);
      return;
    }
    const videoWithoutImage = normalized.localizedData.find((x) => String(x.videoUrl || "").trim() && !String(x.imageUrl || "").trim());
    if (videoWithoutImage) {
      setError(`❌ ${getRecommendationLanguageLabel(videoWithoutImage.languageCode)} için video kartında gösterilecek bir fotoğraf yükleyin.`);
      return;
    }
    setPrefs((previous) => {
      const field = recommendationDraftCatalog === "reservationComplete"
        ? "reservationCompleteExtraProducts"
        : "recommendedProducts";
      const list = normalizeRecommendedProducts(previous[field]);
      const index = list.findIndex((x) => x.id === normalized.id);
      const next = [...list];
      if (index >= 0) next[index] = normalized;
      else next.push(normalized);
      return { ...previous, [field]: next };
    });
    setRecommendationDraft(null);
    setError("");
  };

  const deleteRecommendation = (id, catalog = "recommended") => {
    const field = catalog === "reservationComplete" ? "reservationCompleteExtraProducts" : "recommendedProducts";
    setPrefs((previous) => ({
      ...previous,
      [field]: normalizeRecommendedProducts(previous[field]).filter((x) => x.id !== id),
    }));
  };

  const uploadRecommendationMedia = async (event, mediaType) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !recommendationDraft) return;
    const code = normalizeRecommendationLanguageCode(activeRecommendationLanguage);
    const expected = mediaType === "video" ? "video/" : "image/";
    if (!String(file.type || "").startsWith(expected)) {
      setError(mediaType === "video" ? "❌ Geçerli bir video dosyası seçin." : "❌ Geçerli bir fotoğraf seçin.");
      return;
    }
    setUploadingRecommendationMedia(true);
    setError("");
    try {
      const base64 = await recommendationFileToBase64(file);
      const response = await apiPost("/uploadproductmedia", {
        card: getAuthCard(),
        mediaKind: mediaType,
        mediaScope: "recommendation",
        uniqcode: recommendationDraftCatalog === "reservationComplete" ? `reservation_complete_${recommendationDraft.id}` : recommendationDraft.id,
        languageCode: code,
        contentType: file.type || (mediaType === "video" ? "video/mp4" : "image/jpeg"),
        filename: file.name || `recommendation-${Date.now()}`,
        base64,
      });
      const url = response?.result?.url || response?.publicUrl || response?.url || "";
      if (!url) throw new Error("Medya URL alınamadı");
      updateRecommendationDraftLocale(code, (locale) => {
        const nextImageUrl = mediaType === "image" ? url : String(locale?.imageUrl || "");
        const nextVideoUrl = mediaType === "video" ? url : String(locale?.videoUrl || "");
        return {
          ...locale,
          imageUrl: nextImageUrl,
          videoUrl: nextVideoUrl,
          mediaType: nextVideoUrl ? "video" : nextImageUrl ? "image" : "none",
          mediaUrl: nextVideoUrl || nextImageUrl,
          videoId: nextVideoUrl
            ? (mediaType === "video" ? String(response?.result?.videoId || response?.videoId || "") : String(locale?.videoId || ""))
            : "",
          mediaVersion: Math.max(1, Number(locale?.mediaVersion || 1)) + 1,
          mediaUpdatedAt: new Date().toISOString(),
        };
      });
    } catch (e) {
      setError(`❌ Medya yüklenemedi: ${e?.message || "Bilinmeyen hata"}`);
    } finally {
      setUploadingRecommendationMedia(false);
    }
  };

  const updateHeadingText = (languageCode, text) => {
    const code = normalizeRecommendationLanguageCode(languageCode);
    setPrefs((previous) => {
      const localized = normalizeRecommendationHeadingLocalizedData(previous.recommendedHeadingLocalizedData);
      const index = localized.findIndex((x) => x.languageCode === code);
      const row = { languageCode: code, language: getRecommendationLanguageLabel(code), text: String(text || "").slice(0, 180) };
      const next = [...localized];
      if (index >= 0) next[index] = row;
      else next.push(row);
      return { ...previous, recommendedHeadingLocalizedData: next };
    });
  };

  const addHeadingLanguage = () => {
    if (!headingLanguageToAdd) return;
    const code = normalizeRecommendationLanguageCode(headingLanguageToAdd);
    updateHeadingText(code, "");
    setHeadingLanguage(code);
    setHeadingLanguageToAdd("");
  };

  const removeHeadingLanguage = (code) => {
    if (code === "tr") return;
    setPrefs((previous) => ({
      ...previous,
      recommendedHeadingLocalizedData: normalizeRecommendationHeadingLocalizedData(previous.recommendedHeadingLocalizedData).filter((x) => x.languageCode !== code),
    }));
    setHeadingLanguage((current) => current === code ? "tr" : current);
  };

  const updateReservationCompleteHeadingText = (languageCode, text) => {
    const code = normalizeRecommendationLanguageCode(languageCode);
    setPrefs((previous) => {
      const localized = normalizeRecommendationHeadingLocalizedData(previous.reservationCompleteExtraHeadingLocalizedData);
      const index = localized.findIndex((x) => x.languageCode === code);
      const row = { languageCode: code, language: getRecommendationLanguageLabel(code), text: String(text || "").slice(0, 180) };
      const next = [...localized];
      if (index >= 0) next[index] = row;
      else next.push(row);
      return { ...previous, reservationCompleteExtraHeadingLocalizedData: next };
    });
  };

  const addReservationCompleteHeadingLanguage = () => {
    if (!reservationCompleteHeadingLanguageToAdd) return;
    const code = normalizeRecommendationLanguageCode(reservationCompleteHeadingLanguageToAdd);
    updateReservationCompleteHeadingText(code, "");
    setReservationCompleteHeadingLanguage(code);
    setReservationCompleteHeadingLanguageToAdd("");
  };

  const removeReservationCompleteHeadingLanguage = (code) => {
    if (code === "tr") return;
    setPrefs((previous) => ({
      ...previous,
      reservationCompleteExtraHeadingLocalizedData: normalizeRecommendationHeadingLocalizedData(previous.reservationCompleteExtraHeadingLocalizedData).filter((x) => x.languageCode !== code),
    }));
    setReservationCompleteHeadingLanguage((current) => current === code ? "tr" : current);
  };

  const requestNavigate = useCallback(
    (path) => {
      if (loading) return;
      if (hasChanges) {
        setPendingPath(path);
        setConfirmOpen(true);
      } else {
        navigate(path);
      }
    },
    [hasChanges, navigate, loading]
  );

  const closeConfirm = () => {
    setConfirmOpen(false);
    setPendingPath(null);
  };

  const discardAndContinue = () => {
    const path = pendingPath;
    closeConfirm();
    if (path) navigate(path);
  };

  const saveAndContinue = async () => {
    const ok = await handleSave();
    if (!ok) return;
    const path = pendingPath;
    closeConfirm();
    if (path) navigate(path);
  };

  if (loading) {
    return <FullscreenLoading />;
  }

  const desc2 =
    "Nakit ve yemek kartı siparişlerinde müşteri kasada ödeme yaptıktan sonra ve ardından uygulama ile açılan siparişin onaylanmasını beklemeden KDS sisteminin (mutfakta sipariş görüntüleme sistemi) nin siparişi onaylamasını istiyor musunuz?\n\n" +
    "Aktif derseniz uygulamada onay vermeden KDS sisteminde sipariş ödeme tamamlanmış olarak gösterilecektir.\n" +
    "İnaktif yaparsanız, uygulamada ödeme tamamlandı denene kadar KDS sisteminde sipariş ödeme tamamlandı olarak gösterilmeyecektir ve sipariş beklemede kalacaktır.\n" +
    "Eğer 1 saat içerisinde ödeme tamamlanmaz ise iptal edilen sipariş statüsüne düşürülecektir.";

  const HeaderActions = (
    <div className="flex flex-wrap gap-3">
      <button
        onClick={handleSave}
        disabled={!hasChanges || saving}
        className={[
          "rounded-2xl px-5 py-3 font-semibold text-white shadow-sm transition",
          !hasChanges || saving
            ? "bg-gray-300 cursor-not-allowed"
            : "bg-indigo-600 hover:bg-indigo-700 hover:shadow-md",
        ].join(" ")}
      >
        {saving ? "Kaydediliyor..." : "Kaydet"}
      </button>

      <button
        onClick={() => requestNavigate("/ReceiptInfoPage")}
        className="rounded-2xl px-5 py-3 font-semibold text-white shadow-sm transition bg-purple-600 hover:bg-purple-700 hover:shadow-md"
      >
        Fiş Bilgi
      </button>

      <button
        onClick={() => requestNavigate("/excelfis")}
        className="rounded-2xl px-5 py-3 font-semibold text-white shadow-sm transition bg-amber-500 hover:bg-amber-600 hover:shadow-md"
      >
        Excel Fiş
      </button>

      <button
        onClick={() => requestNavigate("/branches")}
        className="rounded-2xl px-5 py-3 font-semibold text-white shadow-sm transition bg-slate-800 hover:bg-slate-900 hover:shadow-md"
      >
        Şubeler
      </button>

      <button
        onClick={() => requestNavigate("/appointments")}
        className="rounded-2xl px-5 py-3 font-semibold text-white shadow-sm transition bg-fuchsia-600 hover:bg-fuchsia-700 hover:shadow-md"
      >
        Randevular
      </button>

      <button
        onClick={() => requestNavigate("/")}
        className="rounded-2xl px-5 py-3 font-semibold text-white shadow-sm transition bg-emerald-600 hover:bg-emerald-700 hover:shadow-md"
      >
        Ürün Paneli
      </button>

      <button
        onClick={() => requestNavigate("/designpanel")}
        className="rounded-2xl px-5 py-3 font-semibold text-white shadow-sm transition bg-sky-600 hover:bg-sky-700 hover:shadow-md"
      >
        Dizayn Paneli
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <ConfirmLeaveModal
        open={confirmOpen}
        onClose={closeConfirm}
        onDiscard={discardAndContinue}
        onSaveAndContinue={saveAndContinue}
        hasChanges={hasChanges}
        saving={saving}
      />

      {recommendationDraft && (() => {
        const normalizedDraft = normalizeRecommendationProduct(recommendationDraft);
        const locale = normalizedDraft.localizedData.find((x) => x.languageCode === activeRecommendationLanguage) || normalizedDraft.localizedData[0];
        const used = new Set(normalizedDraft.localizedData.map((x) => x.languageCode));
        const discount = recommendationDiscountBreakdown(locale);
        const net = locale?.basePrice > 0 ? locale.basePrice / (1 + Number(locale.kdv || 0) / 100) : 0;
        return (
          <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={() => !uploadingRecommendationMedia && setRecommendationDraft(null)} />
            <div className="relative max-h-[94vh] w-full max-w-5xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
              <div className="flex flex-col gap-4 border-b pb-5 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <h2 className="text-3xl font-extrabold text-gray-900">{recommendationDraftCatalog === "reservationComplete" ? "Rezervasyon Butonu Ekstrası" : "Önerilen Ürün"}</h2>
                  <p className="mt-1 text-sm text-gray-500">Her dilin adı, açıklaması, fiyatı, KDV’si, departmanı, Personel/Saat/Gün ayarları ve medyası birbirinden bağımsızdır.</p>
                </div>
                <button type="button" onClick={() => setRecommendationDraft(null)} disabled={uploadingRecommendationMedia} className="rounded-xl bg-gray-100 px-4 py-2 font-bold text-gray-700 disabled:opacity-40">Kapat</button>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                {normalizedDraft.localizedData.map((row) => (
                  <div key={row.languageCode} className="flex items-center overflow-hidden rounded-xl border border-violet-200">
                    <button type="button" onClick={() => setActiveRecommendationLanguage(row.languageCode)} className={`px-4 py-2 text-sm font-bold ${activeRecommendationLanguage === row.languageCode ? "bg-violet-600 text-white" : "bg-white text-violet-800"}`}>{row.language}</button>
                    {row.languageCode !== "tr" && <button type="button" onClick={() => removeRecommendationLanguage(row.languageCode)} className="bg-red-50 px-2 py-2 text-sm font-bold text-red-700">×</button>}
                  </div>
                ))}
                <select value={recommendationLanguageToAdd} onChange={(e) => setRecommendationLanguageToAdd(e.target.value)} className="rounded-xl border border-emerald-300 bg-white px-3 py-2 text-sm font-bold text-emerald-800">
                  <option value="">+ Dil Ekle</option>
                  {RECOMMENDATION_LANGUAGE_OPTIONS.filter((x) => !used.has(x.code)).map((x) => <option key={x.code} value={x.code}>{x.label}</option>)}
                </select>
                <button type="button" onClick={addRecommendationLanguage} disabled={!recommendationLanguageToAdd} className="rounded-xl bg-emerald-100 px-3 py-2 text-sm font-bold text-emerald-800 disabled:opacity-40">Ekle</button>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div className="space-y-5">
                  <div>
                    <label className="mb-1 block text-sm font-bold text-gray-700">Ürün Adı · {locale.language}</label>
                    <input value={locale.name} onChange={(e) => updateRecommendationDraftLocale(locale.languageCode, { name: e.target.value })} className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 text-lg font-bold outline-none focus:border-violet-500" placeholder="Örn. Aromaterapi Yağı" />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-bold text-gray-700">Açıklama · {locale.language}</label>
                    <textarea value={locale.description} onChange={(e) => updateRecommendationDraftLocale(locale.languageCode, { description: e.target.value })} className="min-h-32 w-full rounded-xl border-2 border-gray-200 px-4 py-3 outline-none focus:border-violet-500" placeholder="İlk eklemede fotoğraf/video altında gösterilecek açıklama..." />
                  </div>

                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                    <div className="mb-2 text-sm font-extrabold text-emerald-950">Fiyat · KDV Dahil Girilir</div>
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="w-32 rounded-xl border bg-white px-3 py-2"><div className="text-[10px] text-gray-500">KDVsiz</div><div className="font-bold">₺{net.toFixed(4)}</div></div>
                      <span className="font-bold text-gray-400">×</span>
                      <select value={locale.kdv} onChange={(e) => updateRecommendationDraftLocale(locale.languageCode, { kdv: Number(e.target.value) })} className="rounded-xl border bg-white px-3 py-3 font-bold">
                        {RECOMMENDATION_VAT_RATES.map((rate) => <option key={rate} value={rate}>%{rate}</option>)}
                      </select>
                      <span className="font-bold text-gray-400">=</span>
                      <div className="flex w-40 items-center rounded-xl border-2 border-emerald-300 bg-white px-3 py-2"><span className="mr-2">₺</span><input type="number" min="0" step="0.01" value={locale.basePrice} onChange={(e) => updateRecommendationDraftLocale(locale.languageCode, { basePrice: e.target.value })} className="w-full bg-transparent text-lg font-extrabold text-emerald-700 outline-none" /></div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <button type="button" onClick={() => updateRecommendationDraftLocale(locale.languageCode, { discountEnabled: !locale.discountEnabled })} className={`rounded-xl px-4 py-2 text-sm font-extrabold ${locale.discountEnabled ? "bg-rose-600 text-white" : "border border-rose-300 bg-white text-rose-700"}`}>{locale.discountEnabled ? "İndirim Açık" : "İndirim Kapalı"}</button>
                      {locale.discountEnabled && <label className="flex items-center gap-2 font-bold text-rose-900">% <input type="number" min="0" max="100" step="0.01" value={locale.discountRate} onChange={(e) => updateRecommendationDraftLocale(locale.languageCode, { discountRate: e.target.value })} className="w-24 rounded-xl border border-rose-300 px-3 py-2" /></label>}
                    </div>
                    {locale.discountEnabled && <div className="mt-3 text-sm text-rose-900"><div>Normal: <b>₺{discount.original.toFixed(2)}</b></div><div>İndirim: <b>%{discount.rate} · -₺{discount.amount.toFixed(2)}</b></div><div className="text-emerald-700">İndirimli KDV Dahil: <b>₺{discount.effective.toFixed(2)}</b></div></div>}
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-bold text-gray-700">Ekstra Departmanı</label>
                    <select value={locale.departmanno} onChange={(e) => updateRecommendationDraftLocale(locale.languageCode, { departmanno: e.target.value })} className="w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-3">
                      <option value="">{departments.length ? "Departman seçin" : "Önce Fiş Bilgi sayfasından departman ekleyin"}</option>
                      {departments.map((department) => <option key={department.number} value={department.number}>{department.label}</option>)}
                    </select>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <h3 className="text-sm font-extrabold text-slate-900">Bu Dile Özel Rezervasyon Seçimleri</h3>
                    <p className="mt-1 text-xs text-slate-500">Türkçede açık olan Personel / Saat / Gün seçimi başka bir dilde kapalı olabilir.</p>
                    <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                      <button
                        type="button"
                        onClick={() => updateRecommendationDraftLocale(locale.languageCode, {
                          personnelEnabled: !locale.personnelEnabled,
                          dayEnabled: locale.personnelEnabled ? !!locale.dayEnabled : false,
                          durationValue: !locale.personnelEnabled && locale.dayEnabled ? "" : locale.durationValue,
                        })}
                        className={`rounded-xl border p-3 text-left font-bold transition ${locale.personnelEnabled ? "border-emerald-500 bg-emerald-50 text-emerald-900" : "border-slate-300 bg-white text-slate-600"}`}
                      >
                        <div className="flex items-center justify-between"><span>Personel</span><span>{locale.personnelEnabled ? "Açık" : "Kapalı"}</span></div>
                      </button>
                      <button
                        type="button"
                        onClick={() => updateRecommendationDraftLocale(locale.languageCode, {
                          timeEnabled: !locale.timeEnabled,
                          dayEnabled: locale.timeEnabled ? !!locale.dayEnabled : false,
                          durationValue: !locale.timeEnabled && locale.dayEnabled ? "" : locale.durationValue,
                        })}
                        className={`rounded-xl border p-3 text-left font-bold transition ${locale.timeEnabled ? "border-blue-500 bg-blue-50 text-blue-900" : "border-slate-300 bg-white text-slate-600"}`}
                      >
                        <div className="flex items-center justify-between"><span>Saat</span><span>{locale.timeEnabled ? "Açık" : "Kapalı"}</span></div>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const nextDay = !locale.dayEnabled;
                          updateRecommendationDraftLocale(locale.languageCode, {
                            dayEnabled: nextDay,
                            personnelEnabled: nextDay ? false : !!locale.personnelEnabled,
                            timeEnabled: nextDay ? false : !!locale.timeEnabled,
                            durationValue: nextDay ? "" : locale.durationValue,
                          });
                        }}
                        className={`rounded-xl border p-3 text-left font-bold transition ${locale.dayEnabled ? "border-violet-500 bg-violet-50 text-violet-900" : "border-slate-300 bg-white text-slate-600"}`}
                      >
                        <div className="flex items-center justify-between"><span>Gün</span><span>{locale.dayEnabled ? "Açık" : "Kapalı"}</span></div>
                      </button>
                    </div>
                    {locale.dayEnabled && <p className="mt-3 rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 text-xs font-semibold text-violet-900">Gün açık olduğunda Personel ve Saat kapatılır. Süre alanında gün/ay seçenekleri kullanılır.</p>}
                  </div>

                  <div className="rounded-2xl border border-orange-200 bg-orange-50 p-4">
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={() => updateRecommendationDraftLocale(locale.languageCode, { hasQuantity: !locale.hasQuantity })} className={`rounded-xl px-3 py-2 text-sm font-bold ${locale.hasQuantity ? "bg-blue-600 text-white" : "bg-white text-gray-700"}`}>{locale.hasQuantity ? "Adet Var" : "Adet Yok"}</button>
                      <button type="button" onClick={() => updateRecommendationDraftLocale(locale.languageCode, { durationEnabled: !locale.durationEnabled })} className={`rounded-xl px-3 py-2 text-sm font-bold ${locale.durationEnabled ? "bg-orange-500 text-white" : "bg-white text-gray-700"}`}>{locale.durationEnabled ? "Süre Açık" : "Süre Kapalı"}</button>
                      <button type="button" onClick={() => updateRecommendationDraftLocale(locale.languageCode, { durationVisible: !locale.durationVisible })} className={`rounded-xl px-3 py-2 text-sm font-bold ${locale.durationVisible ? "bg-emerald-600 text-white" : "bg-white text-gray-700"}`}>{locale.durationVisible ? "Süreyi Göster" : "Süreyi Gösterme"}</button>
                    </div>
                    {locale.durationEnabled && <div className="mt-3"><label className="mb-1 block text-xs font-bold text-orange-900">Öneri Süresi {locale.dayEnabled ? "· Gün / Ay" : "· Dakika / Saat"}</label><select value={locale.durationValue} onChange={(e) => updateRecommendationDraftLocale(locale.languageCode, { durationValue: e.target.value })} className="w-full rounded-xl border border-orange-300 bg-white px-3 py-2">{getRecommendationDurationOptions(!!locale.dayEnabled).map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}</select></div>}
                  </div>
                </div>

                <div>
                  <div className="rounded-2xl border-2 border-purple-200 bg-purple-50 p-5">
                    <div className="text-xl font-extrabold text-purple-950">Kart Fotoğrafı + Tanıtım Videosu · {locale.language}</div>
                    <p className="mt-1 text-sm text-purple-700">Kartta her zaman fotoğraf gösterilir. Video yüklüyse müşteri karta dokunduğunda açılan açıklama ekranında video oynar; video yoksa fotoğraf gösterilir.</p>

                    <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
                      <div className="rounded-2xl border border-purple-200 bg-white p-4">
                        <div className="mb-3 font-extrabold text-purple-900">📷 Kart Fotoğrafı</div>
                        <div className="aspect-video overflow-hidden rounded-xl bg-slate-100 flex items-center justify-center">
                          {locale.imageUrl ? <img src={locale.imageUrl} alt={locale.name} className="h-full w-full object-cover" /> : <div className="text-center font-bold text-slate-400">Fotoğraf yüklenmedi</div>}
                        </div>
                        <label className={`mt-3 block cursor-pointer rounded-xl px-4 py-3 text-center font-extrabold text-white ${uploadingRecommendationMedia ? "bg-purple-300" : "bg-purple-600 hover:bg-purple-700"}`}>{uploadingRecommendationMedia ? "Yükleniyor..." : "Fotoğraf Yükle / Değiştir"}<input type="file" className="hidden" disabled={uploadingRecommendationMedia} accept="image/*" onChange={(e) => uploadRecommendationMedia(e, "image")} /></label>
                        {locale.imageUrl && <button type="button" onClick={() => updateRecommendationDraftLocale(locale.languageCode, (row) => ({ ...row, imageUrl: "", mediaType: row.videoUrl ? "video" : "none", mediaUrl: row.videoUrl || "" }))} className="mt-2 w-full rounded-xl bg-red-50 px-4 py-2 font-bold text-red-700">Fotoğrafı Kaldır</button>}
                      </div>

                      <div className="rounded-2xl border border-purple-200 bg-white p-4">
                        <div className="mb-3 font-extrabold text-purple-900">🎬 Tanıtım Videosu <span className="text-sm font-semibold text-purple-500">(opsiyonel)</span></div>
                        <div className="aspect-video overflow-hidden rounded-xl bg-slate-900 flex items-center justify-center">
                          {locale.videoUrl ? <video src={locale.videoUrl} controls preload="metadata" className="h-full w-full object-contain" /> : <div className="text-center font-bold text-white/50">Video yüklenmedi</div>}
                        </div>
                        <label className={`mt-3 block cursor-pointer rounded-xl px-4 py-3 text-center font-extrabold text-white ${uploadingRecommendationMedia ? "bg-purple-300" : "bg-purple-600 hover:bg-purple-700"}`}>{uploadingRecommendationMedia ? "Yükleniyor..." : "Video Yükle / Değiştir"}<input type="file" className="hidden" disabled={uploadingRecommendationMedia} accept="video/*" onChange={(e) => uploadRecommendationMedia(e, "video")} /></label>
                        {locale.videoUrl && <button type="button" onClick={() => updateRecommendationDraftLocale(locale.languageCode, (row) => ({ ...row, videoUrl: "", videoId: "", mediaType: row.imageUrl ? "image" : "none", mediaUrl: row.imageUrl || "" }))} className="mt-2 w-full rounded-xl bg-red-50 px-4 py-2 font-bold text-red-700">Videoyu Kaldır</button>}
                      </div>
                    </div>

                    {locale.videoUrl && !locale.imageUrl && <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm font-bold text-amber-800">Video yüklüyse kart üzerinde göstermek için fotoğraf da yüklemelisiniz.</div>}

                    <div className="mt-4 rounded-xl border border-purple-200 bg-white p-4 text-sm text-purple-900">
                      Müşteri ürüne ilk kez dokunduğunda açıklama modalı açılır. Video varsa video oynatılır; video yoksa kart fotoğrafı gösterilir. Aynı sipariş oturumunda ürün çıkarılıp tekrar eklense bile tanıtım ikinci kez açılmaz.
                    </div>
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 mt-6 flex justify-end gap-3 border-t bg-white/95 pt-5 backdrop-blur">
                <button type="button" onClick={() => setRecommendationDraft(null)} className="rounded-2xl bg-gray-100 px-6 py-3 font-bold text-gray-700">İptal</button>
                <button type="button" onClick={saveRecommendationDraft} disabled={uploadingRecommendationMedia} className="rounded-2xl bg-emerald-600 px-7 py-3 font-extrabold text-white shadow hover:bg-emerald-700 disabled:opacity-50">{recommendationDraftCatalog === "reservationComplete" ? "Rezervasyon Ekstrasını Kaydet" : "Önerilen Ürünü Kaydet"}</button>
              </div>
            </div>
          </div>
        );
      })()}


      <div className="max-w-5xl mx-auto px-6 py-10">
        {showHeader ? (
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">
                {title}
              </h1>
              <p className="text-gray-600 mt-1">
                Kiosk ödeme ve KDS davranışlarını buradan yönetebilirsiniz.
              </p>
            </div>

            {HeaderActions}
          </div>
        ) : null}

        {error ? (
          <div className="mb-5 rounded-2xl border-2 border-red-200 bg-red-50 px-5 py-4 text-red-800">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">{error}</div>
              <button
                onClick={() => loadFromBackend({})}
                className="shrink-0 rounded-2xl bg-red-600 hover:bg-red-700 text-white px-4 py-2 font-semibold transition"
              >
                Tekrar Dene
              </button>
            </div>
          </div>
        ) : null}

        {toast ? (
          <div className="mb-5 rounded-2xl border-2 border-emerald-200 bg-emerald-50 px-5 py-4 text-emerald-800">
            {toast}
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-6">
          <PreferenceCard
            title="Nakit & Yemek Kartı: KDS “ödeme tamamlandı” otomatik olsun mu?"
            description={desc2}
          >
            <ToggleSwitch
              disabled={saving}
              checked={prefs.kdsAutoPaidOnCashOrMeal}
              onChange={(v) =>
                setPrefs((p) => ({ ...p, kdsAutoPaidOnCashOrMeal: v }))
              }
            />
          </PreferenceCard>

          {EXTRA_PAYMENT_OPTIONS.map((item) => (
            <PreferenceCard
              key={item.stateKey}
              title={item.title}
              description={item.description}
            >
              <ToggleSwitch
                disabled={saving}
                checked={!!prefs[item.stateKey]}
                onChange={(v) =>
                  setPrefs((p) => ({
                    ...p,
                    [item.stateKey]: v,
                  }))
                }
              />
            </PreferenceCard>
          ))}


          <div className="rounded-3xl border-2 border-violet-200 bg-violet-50 p-6 shadow-sm">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div>
                <h2 className="text-2xl font-extrabold text-violet-950">Ortak Önerilen Ürünler</h2>
                <p className="mt-2 max-w-3xl text-sm leading-relaxed text-violet-800">
                  Bu katalog bütün ana ürünlerde ortaktır. Bir ana ürünün düzenleme sayfasındaki “Önerilen Ürünler Gözüksün mü?” ayarı Evet ise müşteri o ürünü ekledikten sonra buradaki ürünleri görür.
                </p>
              </div>
              <button
                type="button"
                onClick={openNewRecommendation}
                disabled={saving}
                className="rounded-2xl bg-violet-600 px-5 py-3 font-bold text-white shadow hover:bg-violet-700 disabled:opacity-50"
              >
                + Önerilen Ürün Ekle
              </button>
            </div>

            <div className="mt-6 rounded-2xl border border-violet-200 bg-white p-5">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                <div className="flex-1">
                  <label className="mb-2 block text-sm font-bold text-gray-800">Kioskta üstte görünecek büyük yazı</label>
                  <div className="mb-3 flex flex-wrap gap-2">
                    {normalizeRecommendationHeadingLocalizedData(prefs.recommendedHeadingLocalizedData).map((row) => (
                      <button
                        key={row.languageCode}
                        type="button"
                        onClick={() => setHeadingLanguage(row.languageCode)}
                        className={`rounded-xl px-3 py-2 text-sm font-bold ${headingLanguage === row.languageCode ? "bg-violet-600 text-white" : "bg-gray-100 text-gray-700"}`}
                      >
                        {row.language}
                      </button>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={normalizeRecommendationHeadingLocalizedData(prefs.recommendedHeadingLocalizedData).find((x) => x.languageCode === headingLanguage)?.text || ""}
                    onChange={(e) => updateHeadingText(headingLanguage, e.target.value)}
                    placeholder="Bu hizmeti alanlar bunları da ekledi"
                    className="w-full rounded-2xl border-2 border-violet-200 px-4 py-3 text-lg font-bold outline-none focus:border-violet-500"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={headingLanguageToAdd}
                    onChange={(e) => setHeadingLanguageToAdd(e.target.value)}
                    className="rounded-xl border border-violet-300 bg-white px-3 py-2 text-sm"
                  >
                    <option value="">+ Başlık Dili Ekle</option>
                    {RECOMMENDATION_LANGUAGE_OPTIONS.filter((x) => !normalizeRecommendationHeadingLocalizedData(prefs.recommendedHeadingLocalizedData).some((row) => row.languageCode === x.code)).map((x) => (
                      <option key={x.code} value={x.code}>{x.label}</option>
                    ))}
                  </select>
                  <button type="button" onClick={addHeadingLanguage} disabled={!headingLanguageToAdd} className="rounded-xl bg-violet-100 px-3 py-2 text-sm font-bold text-violet-800 disabled:opacity-40">Ekle</button>
                  {headingLanguage !== "tr" && (
                    <button type="button" onClick={() => removeHeadingLanguage(headingLanguage)} className="rounded-xl bg-red-50 px-3 py-2 text-sm font-bold text-red-700">Bu Dili Sil</button>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
              {normalizeRecommendedProducts(prefs.recommendedProducts).map((item) => {
                const tr = item.localizedData.find((x) => x.languageCode === "tr") || item.localizedData[0];
                const discount = recommendationDiscountBreakdown(tr);
                return (
                  <div key={item.id} className="mx-auto w-full max-w-[300px] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl">
                    <button type="button" onClick={() => openEditRecommendation(item)} className="group block w-full text-left">
                      <div className="relative aspect-video overflow-hidden bg-gray-100">
                        {tr?.imageUrl ? (
                          <div className="relative h-full w-full bg-slate-100">
                            <img src={tr.imageUrl} alt={tr.name} className="h-full w-full object-cover" />
                          </div>
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-lg font-bold text-gray-400">Medya Yok</div>
                        )}
                        <div className="absolute right-3 top-3 max-w-[160px] rounded-2xl bg-white/95 px-3 py-1.5 text-center text-sm font-black text-gray-900 shadow-lg">
                          {discount.rate > 0 ? (
                            <div className="leading-tight">
                              <div className="text-xs font-bold text-gray-400 line-through">₺{discount.original.toFixed(2)}</div>
                              <div className="text-rose-600">₺{discount.effective.toFixed(2)} · %{discount.rate}</div>
                            </div>
                          ) : (
                            <>₺{discount.effective.toFixed(2)}</>
                          )}
                        </div>
                      </div>

                      <div className="px-4 py-3">
                        <div className="line-clamp-2 min-h-[42px] text-lg font-extrabold leading-tight text-gray-900 transition-colors group-hover:text-violet-700">{tr?.name || "İsimsiz öneri"}</div>
                        <div className="mt-1 line-clamp-2 min-h-[34px] text-xs leading-relaxed text-gray-500">{tr?.description || "Açıklama eklenmedi."}</div>
                        <div className="mt-2.5 flex items-center justify-between gap-3">
                          <div className="text-sm font-extrabold text-violet-700">Önerilen Hizmet</div>
                          <div className="text-right text-xs font-semibold text-gray-500">{item.languages.map(getRecommendationLanguageLabel).join(" · ")}</div>
                        </div>
                        <div className="mt-2.5 flex flex-wrap gap-1.5 text-[11px] font-bold">
                          {tr?.personnelEnabled && <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-800">Personel</span>}
                          {tr?.timeEnabled && <span className="rounded-full bg-blue-100 px-3 py-1 text-blue-800">Saat</span>}
                          {tr?.dayEnabled && <span className="rounded-full bg-violet-100 px-3 py-1 text-violet-800">Gün</span>}
                          {tr?.hasQuantity && <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-800">Adet</span>}
                        </div>
                      </div>
                    </button>
                    <div className="flex gap-3 border-t border-gray-100 p-3">
                      <button type="button" onClick={() => openEditRecommendation(item)} className="flex-1 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-extrabold text-white shadow hover:bg-violet-700">Düzenle</button>
                      <button type="button" onClick={() => deleteRecommendation(item.id)} className="rounded-xl bg-red-50 px-4 py-2.5 text-sm font-extrabold text-red-700 hover:bg-red-100">Sil</button>
                    </div>
                  </div>
                );
              })}
              {normalizeRecommendedProducts(prefs.recommendedProducts).length === 0 && (
                <div className="md:col-span-3 rounded-2xl border-2 border-dashed border-violet-200 bg-white p-8 text-center text-violet-700">
                  Henüz önerilen ürün yok. “Önerilen Ürün Ekle” ile ortak kataloğu oluşturabilirsiniz.
                </div>
              )}
            </div>
          </div>

          <div className="rounded-3xl border-2 border-cyan-200 bg-cyan-50 p-6 shadow-sm">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div>
                <h2 className="text-2xl font-extrabold text-cyan-950">Rezervasyon Butonu Ekstrası</h2>
                <p className="mt-2 max-w-3xl text-sm leading-relaxed text-cyan-800">
                  Müşteri Rezervasyonu Tamamla butonuna bastığında, ödeme ekranından önce bu ikinci ortak ekstra kataloğu gösterilir. Dil, fotoğraf/video, KDV, indirim, adet, Personel/Saat/Gün ve süre ayarları normal önerilen ürünlerle aynıdır.
                </p>
              </div>
              <button
                type="button"
                onClick={() => openNewRecommendation("reservationComplete")}
                disabled={saving}
                className="rounded-2xl bg-cyan-600 px-5 py-3 font-bold text-white shadow hover:bg-cyan-700 disabled:opacity-50"
              >
                + Rezervasyon Ekstrası Ekle
              </button>
            </div>

            <div className="mt-6 rounded-2xl border border-cyan-200 bg-white p-5">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                <div className="flex-1">
                  <label className="mb-2 block text-sm font-bold text-gray-800">Kioskta üstte görünecek büyük yazı</label>
                  <div className="mb-3 flex flex-wrap gap-2">
                    {normalizeRecommendationHeadingLocalizedData(prefs.reservationCompleteExtraHeadingLocalizedData).map((row) => (
                      <button
                        key={row.languageCode}
                        type="button"
                        onClick={() => setReservationCompleteHeadingLanguage(row.languageCode)}
                        className={`rounded-xl px-3 py-2 text-sm font-bold ${reservationCompleteHeadingLanguage === row.languageCode ? "bg-cyan-600 text-white" : "bg-gray-100 text-gray-700"}`}
                      >
                        {row.language}
                      </button>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={normalizeRecommendationHeadingLocalizedData(prefs.reservationCompleteExtraHeadingLocalizedData).find((x) => x.languageCode === reservationCompleteHeadingLanguage)?.text || ""}
                    onChange={(e) => updateReservationCompleteHeadingText(reservationCompleteHeadingLanguage, e.target.value)}
                    placeholder="Rezervasyonunuza bunları da eklemek ister misiniz?"
                    className="w-full rounded-2xl border-2 border-cyan-200 px-4 py-3 text-lg font-bold outline-none focus:border-cyan-500"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={reservationCompleteHeadingLanguageToAdd}
                    onChange={(e) => setReservationCompleteHeadingLanguageToAdd(e.target.value)}
                    className="rounded-xl border border-cyan-300 bg-white px-3 py-2 text-sm"
                  >
                    <option value="">+ Başlık Dili Ekle</option>
                    {RECOMMENDATION_LANGUAGE_OPTIONS.filter((x) => !normalizeRecommendationHeadingLocalizedData(prefs.reservationCompleteExtraHeadingLocalizedData).some((row) => row.languageCode === x.code)).map((x) => (
                      <option key={x.code} value={x.code}>{x.label}</option>
                    ))}
                  </select>
                  <button type="button" onClick={addReservationCompleteHeadingLanguage} disabled={!reservationCompleteHeadingLanguageToAdd} className="rounded-xl bg-cyan-100 px-3 py-2 text-sm font-bold text-cyan-800 disabled:opacity-40">Ekle</button>
                  {reservationCompleteHeadingLanguage !== "tr" && (
                    <button type="button" onClick={() => removeReservationCompleteHeadingLanguage(reservationCompleteHeadingLanguage)} className="rounded-xl bg-red-50 px-3 py-2 text-sm font-bold text-red-700">Bu Dili Sil</button>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
              {normalizeRecommendedProducts(prefs.reservationCompleteExtraProducts).map((item) => {
                const tr = item.localizedData.find((x) => x.languageCode === "tr") || item.localizedData[0];
                const discount = recommendationDiscountBreakdown(tr);
                return (
                  <div key={item.id} className="mx-auto w-full max-w-[300px] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg">
                    <button type="button" onClick={() => openEditRecommendation(item, "reservationComplete")} className="group block w-full text-left">
                      <div className="relative aspect-video overflow-hidden bg-gray-100">
                        {tr?.imageUrl ? (
                          <div className="relative h-full w-full bg-slate-100">
                            <img src={tr.imageUrl} alt={tr.name} className="h-full w-full object-cover" />
                          </div>
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-lg font-bold text-gray-400">Medya Yok</div>
                        )}
                        <div className="absolute right-3 top-3 max-w-[145px] rounded-2xl bg-white/95 px-3 py-1.5 text-center text-xs font-black text-gray-900 shadow-lg">
                          {discount.rate > 0 ? (
                            <div className="leading-tight"><div className="text-[10px] font-bold text-gray-400 line-through">₺{discount.original.toFixed(2)}</div><div className="text-rose-600">₺{discount.effective.toFixed(2)} · %{discount.rate}</div></div>
                          ) : <>₺{discount.effective.toFixed(2)}</>}
                        </div>
                      </div>
                      <div className="px-4 py-3">
                        <div className="line-clamp-2 min-h-[42px] text-lg font-extrabold leading-tight text-gray-900">{tr?.name || "İsimsiz ekstra"}</div>
                        <div className="mt-1 line-clamp-2 min-h-[34px] text-xs leading-relaxed text-gray-500">{tr?.description || "Açıklama eklenmedi."}</div>
                        <div className="mt-2.5 flex items-center justify-between gap-3"><div className="text-xs font-extrabold text-cyan-700">Rezervasyon Ekstrası</div><div className="text-right text-[11px] font-semibold text-gray-500">{item.languages.map(getRecommendationLanguageLabel).join(" · ")}</div></div>
                        <div className="mt-2.5 flex flex-wrap gap-1.5 text-[10px] font-bold">
                          {tr?.personnelEnabled && <span className="rounded-full bg-emerald-100 px-2 py-1 text-emerald-800">Personel</span>}
                          {tr?.timeEnabled && <span className="rounded-full bg-blue-100 px-2 py-1 text-blue-800">Saat</span>}
                          {tr?.dayEnabled && <span className="rounded-full bg-violet-100 px-2 py-1 text-violet-800">Gün</span>}
                          {tr?.hasQuantity && <span className="rounded-full bg-amber-100 px-2 py-1 text-amber-800">Adet</span>}
                        </div>
                      </div>
                    </button>
                    <div className="flex gap-3 border-t border-gray-100 p-3">
                      <button type="button" onClick={() => openEditRecommendation(item, "reservationComplete")} className="flex-1 rounded-xl bg-cyan-600 px-4 py-2.5 text-sm font-extrabold text-white shadow hover:bg-cyan-700">Düzenle</button>
                      <button type="button" onClick={() => deleteRecommendation(item.id, "reservationComplete")} className="rounded-xl bg-red-50 px-4 py-2.5 text-sm font-extrabold text-red-700 hover:bg-red-100">Sil</button>
                    </div>
                  </div>
                );
              })}
              {normalizeRecommendedProducts(prefs.reservationCompleteExtraProducts).length === 0 && (
                <div className="md:col-span-3 rounded-2xl border-2 border-dashed border-cyan-200 bg-white p-8 text-center text-cyan-700">
                  Henüz Rezervasyon Butonu Ekstrası yok. “Rezervasyon Ekstrası Ekle” ile kataloğu oluşturabilirsiniz.
                </div>
              )}
            </div>
          </div>

          <PreferenceCard
            title="EKÜ No"
            description={
              "Cihazınızın hafıza kartını değiştirirseniz burayı güncellemeniz gereklidir."
            }
          >
            <div className="w-full md:w-[220px]">
              <input
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                disabled={saving}
                value={prefs.ekuNo}
                onChange={(e) => {
                  const raw = e.target.value;

                  if (raw === "") {
                    setPrefs((p) => ({ ...p, ekuNo: "" }));
                    return;
                  }

                  const cleaned = raw.replace(/[^\d]/g, "");
                  setPrefs((p) => ({
                    ...p,
                    ekuNo: cleaned,
                  }));
                }}
                onBlur={() => {
                  setPrefs((p) => ({
                    ...p,
                    ekuNo: normalizeEkuNo(p.ekuNo),
                  }));
                }}
                className={[
                  "w-full rounded-2xl border-2 px-4 py-3 text-lg font-semibold outline-none transition",
                  saving
                    ? "border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed"
                    : "border-gray-300 bg-white text-gray-900 focus:border-indigo-500",
                ].join(" ")}
                placeholder="EKÜ No"
              />
            </div>
          </PreferenceCard>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="text-sm text-gray-500">
              {hasChanges ? "Kaydedilmemiş değişiklikler var." : "Her şey güncel."}
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleResetToDefault}
                disabled={saving}
                className={[
                  "rounded-2xl border-2 bg-white px-5 py-3 font-semibold shadow-sm transition",
                  saving
                    ? "border-gray-200 text-gray-400 cursor-not-allowed"
                    : "border-gray-300 text-gray-800 hover:shadow-md",
                ].join(" ")}
              >
                Varsayılana Sıfırla
              </button>

              {!showHeader ? (
                <>
                  <button
                    onClick={handleSave}
                    disabled={!hasChanges || saving}
                    className={[
                      "rounded-2xl px-5 py-3 font-semibold text-white shadow-sm transition",
                      !hasChanges || saving
                        ? "bg-gray-300 cursor-not-allowed"
                        : "bg-indigo-600 hover:bg-indigo-700 hover:shadow-md",
                    ].join(" ")}
                  >
                    {saving ? "Kaydediliyor..." : "Kaydet"}
                  </button>

                  <button
                    onClick={() => requestNavigate("/ReceiptInfoPage")}
                    className="rounded-2xl px-5 py-3 font-semibold text-white shadow-sm transition bg-purple-600 hover:bg-purple-700 hover:shadow-md"
                  >
                    Fiş Bilgi
                  </button>

                  <button
                    onClick={() => requestNavigate("/excelfis")}
                    className="rounded-2xl px-5 py-3 font-semibold text-white shadow-sm transition bg-amber-500 hover:bg-amber-600 hover:shadow-md"
                  >
                    Excel Fiş
                  </button>

                  <button
                    onClick={() => requestNavigate("/branches")}
                    className="rounded-2xl px-5 py-3 font-semibold text-white shadow-sm transition bg-slate-800 hover:bg-slate-900 hover:shadow-md"
                  >
                    Şubeler
                  </button>

                  <button
                    onClick={() => requestNavigate("/appointments")}
                    className="rounded-2xl px-5 py-3 font-semibold text-white shadow-sm transition bg-fuchsia-600 hover:bg-fuchsia-700 hover:shadow-md"
                  >
                    Randevular
                  </button>

                  <button
                    onClick={() => requestNavigate("/")}
                    className="rounded-2xl px-5 py-3 font-semibold text-white shadow-sm transition bg-emerald-600 hover:bg-emerald-700 hover:shadow-md"
                  >
                    Ürün Paneli
                  </button>

                  <button
                    onClick={() => requestNavigate("/designpanel")}
                    className="rounded-2xl px-5 py-3 font-semibold text-white shadow-sm transition bg-sky-600 hover:bg-sky-700 hover:shadow-md"
                  >
                    Dizayn Paneli
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Preferance;